import { Button } from '@/components/ui/button';
import { Printer } from 'lucide-react';

/**
 * Printable code cards — reliable multi-page layout for A4 PDF export.
 *
 * Why explicit page chunking instead of `break-inside: avoid` on a CSS grid:
 *   Chrome (and therefore "Save as PDF") frequently ignores `break-inside: avoid`
 *   on grid/flex children when the printable area is shorter than the chunk.
 *   The bullet-proof fix is to render hard page wrappers with a fixed sub-grid
 *   of N cards each and `page-break-after: always` between wrappers.
 *
 * Layout:
 *   • Teacher card         → its own page (one full-width card centered)
 *   • Student cards        → 6 per A4 page (2 columns × 3 rows)
 *   • When printing multiple classes, each class starts on a fresh page.
 *
 * Print rules:
 *   `@page { size: A4 portrait; margin: 12mm }` removes the browser's default
 *   header/footer boxes once the user unticks "Headers and footers".
 */

const STUDENT_CARDS_PER_PAGE = 6; // 2 columns × 3 rows on A4

function chunkStudents(students) {
  const chunks = [];
  for (let i = 0; i < students.length; i += STUDENT_CARDS_PER_PAGE) {
    chunks.push(students.slice(i, i + STUDENT_CARDS_PER_PAGE));
  }
  return chunks;
}

function StudentCard({ student, className, schoolName, year, siteUrl, t }) {
  return (
    <div className="student-card border-2 border-dashed border-gray-400 rounded-lg p-4 flex flex-col justify-between">
      <div className="text-center">
        <div className="text-[10px] uppercase tracking-widest font-black text-black mb-1">SchoolPix</div>
        <div className="inline-block bg-black text-white text-[11px] font-black uppercase tracking-wider rounded px-2.5 py-1 mb-2">
          {className}
        </div>
        <h3 className="text-base font-black text-black mb-0.5">{student.name}</h3>
        <p className="text-[11px] font-bold text-black mb-3">
          {schoolName} · {year}
        </p>
        <div className="bg-gray-100 rounded-md py-2 px-3 mb-3 border border-gray-200">
          <div className="text-[10px] font-bold uppercase tracking-wider text-black mb-0.5">{t('students.yourCode')}</div>
          <div className="font-mono text-xl font-black tracking-widest text-black">
            {student.unique_code}
          </div>
        </div>
      </div>
      <div className="text-center">
        <p className="text-[10px] font-bold text-black leading-tight">
          {t('students.scanOrVisit')}
        </p>
        <p className="text-[10px] font-mono font-black text-black mt-1 break-all">{siteUrl}</p>
      </div>
    </div>
  );
}

function TeacherCardPage({ cls, siteUrl, t }) {
  return (
    <div className="print-page teacher-page">
      <div className="teacher-card border-2 border-black rounded-lg p-8">
        <div className="text-center">
          <div className="text-xs uppercase tracking-widest font-black text-gray-700 mb-2">SchoolPix</div>
          <div className="inline-block bg-black text-white text-xs font-black uppercase tracking-widest rounded px-3 py-1 mb-3">
            {t('students.teacherCardTitle')}
          </div>
          <h3 className="text-3xl font-black text-black mb-1 tracking-tight">{cls.class_name}</h3>
          <p className="text-base font-bold text-gray-800 mb-2">{cls.school_name} · {cls.year}</p>
          <p className="text-xs font-bold text-gray-700 mb-5">{t('students.teacherCardSubtitle')}</p>
          <div className="bg-gray-100 rounded-md py-5 px-6 mb-4 inline-block border border-gray-200">
            <div className="text-xs uppercase tracking-wider font-bold text-gray-700 mb-2">
              {t('students.teacherCardCode')}
            </div>
            <div className="font-mono text-3xl font-black tracking-widest text-black">
              {cls.teacher_code}
            </div>
          </div>
          <p className="text-xs font-bold text-gray-700 leading-relaxed max-w-md mx-auto">
            {t('students.scanOrVisit')}
          </p>
          <p className="text-xs font-mono font-black text-black mt-1">{siteUrl}</p>
        </div>
      </div>
    </div>
  );
}

function StudentPage({ studentsChunk, cls, siteUrl, t }) {
  return (
    <div className="print-page student-page">
      <div className="student-page-grid">
        {studentsChunk.map(function(student) {
          return (
            <StudentCard
              key={student.student_id}
              student={student}
              className={cls.class_name}
              schoolName={cls.school_name}
              year={cls.year}
              siteUrl={siteUrl}
              t={t}
            />
          );
        })}
      </div>
    </div>
  );
}

export default function CodeCardsPrintView({ data, siteUrl, t, onClose }) {
  // Support both shapes: { classes: [...] } (multi) OR a single-class object.
  const classList = (data && data.classes)
    ? data.classes
    : [data];

  const pages = [];
  classList.forEach(function(cls, classIdx) {
    if (cls.teacher_code) {
      pages.push({ type: 'teacher', cls: cls, key: 'teacher-' + classIdx });
    }
    const chunks = chunkStudents(cls.students || []);
    chunks.forEach(function(chunk, chunkIdx) {
      pages.push({ type: 'students', cls: cls, chunk: chunk, key: 'students-' + classIdx + '-' + chunkIdx });
    });
  });

  const firstClassName = classList.length === 1
    ? classList[0].class_name
    : classList.length + ' ' + t('admin.classes');

  return (
    <div className="fixed inset-0 z-50 bg-white overflow-auto" id="print-cards-view" data-testid="code-cards-print-view">
      <style>{`
        @media print {
          @page {
            size: A4 portrait;
            margin: 12mm;
          }
          /* Safari (and Edge) strip background-color from print output unless
             we explicitly opt in. Without this, the black class-name pill
             badge and the gray code box become invisible — making the new
             print look identical to the old "plain text" layout. */
          * {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
            color-adjust: exact !important;
          }
          html, body { background: white !important; margin: 0 !important; padding: 0 !important; }
          .no-print { display: none !important; }

          #print-cards-view {
            position: static !important;
            inset: auto !important;
            overflow: visible !important;
            height: auto !important;
          }

          .print-root {
            padding: 0 !important;
            max-width: none !important;
            margin: 0 !important;
          }

          /* Each wrapper is exactly one printed page */
          .print-page {
            page-break-inside: avoid;
            break-inside: avoid;
            page-break-after: always;
            break-after: page;
            /* Fill the printable area so the browser treats it as one sheet */
            height: 273mm; /* A4 height (297) minus 2 × 12mm margin */
            width: 100%;
            box-sizing: border-box;
            display: flex;
            flex-direction: column;
            justify-content: flex-start;
          }
          .print-page:last-child {
            page-break-after: auto;
            break-after: auto;
          }

          .teacher-page {
            justify-content: center;
            align-items: center;
          }
          .teacher-card {
            width: 100%;
            max-width: 160mm;
          }

          .student-page-grid {
            display: grid;
            grid-template-columns: 1fr 1fr;
            grid-template-rows: repeat(3, 1fr);
            gap: 8mm;
            width: 100%;
            height: 100%;
          }
          .student-card {
            break-inside: avoid;
            page-break-inside: avoid;
          }
        }

        /* Screen preview: show pages stacked with a small visual gap */
        @media screen {
          .print-page {
            background: white;
            padding: 12mm;
            margin: 0 auto 20px;
            max-width: 210mm;
            min-height: 297mm;
            box-shadow: 0 2px 12px rgba(0,0,0,0.15);
            box-sizing: border-box;
          }
          .teacher-page {
            display: flex;
            align-items: center;
            justify-content: center;
          }
          .teacher-card { width: 100%; max-width: 160mm; }
          .student-page-grid {
            display: grid;
            grid-template-columns: 1fr 1fr;
            grid-template-rows: repeat(3, 1fr);
            gap: 8mm;
          }
        }
      `}</style>

      <div className="no-print sticky top-0 z-10 bg-white border-b p-4 flex items-center justify-between">
        <h2 className="text-lg font-bold text-black">
          {firstClassName} — {t('students.printCodes')}
        </h2>
        <div className="flex gap-3">
          <Button onClick={function() { window.print(); }} className="bg-black text-white hover:bg-gray-800" data-testid="print-cards-btn">
            <Printer className="w-4 h-4 mr-2" />
            {t('students.printPage')}
          </Button>
          <Button variant="outline" onClick={onClose} className="border-gray-300 text-black" data-testid="close-cards-btn">
            {t('common.close')}
          </Button>
        </div>
      </div>

      <div className="print-root py-6 bg-white">
        {pages.map(function(page) {
          if (page.type === 'teacher') {
            return <TeacherCardPage key={page.key} cls={page.cls} siteUrl={siteUrl} t={t} />;
          }
          return <StudentPage key={page.key} studentsChunk={page.chunk} cls={page.cls} siteUrl={siteUrl} t={t} />;
        })}
      </div>
    </div>
  );
}
